import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Badge, Button, Group, Modal, Text } from "@mantine/core";

import { StyledEditModalContent } from "@pages/Patients/Prescription/components/EditModal/EditModal.styled";

import { handleOnChange } from "@utils/handleOnChange";

import { useCreateMedication } from "@hooks/mutations/prescription/useCreateMedication";
import { useCreatePrescription } from "@hooks/mutations/prescription/useCreatePrescription";

import { Consultation } from "@customTypes/consultation";
import { Medication } from "@customTypes/prescription";


interface AddModalProps {
  shouldCreatePrescription?: boolean;
  generalConsultationInfo?: Partial<Consultation> & { userId: string };
  prescriptionId: string;
  cId: string;
  opened: boolean;
  close: () => void;
}

export const AddModal = ({
  opened,
  close,
  prescriptionId,
  cId,
  shouldCreatePrescription = false,
  generalConsultationInfo,
}: AddModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({} as Medication);
  const { patientId } = useParams();

  const { mutate: createMedication, isLoading: isCreateMedicationLoading } =
    useCreateMedication();
  const { mutate: createPrescription, isLoading: isCreatePrescriptionLoading } =
    useCreatePrescription();

  const handleCreateMedication = () => {
    import.meta.env.DEV && console.log("Create medication:", formData);

    if (shouldCreatePrescription) {
      const jsFormData = new FormData();
      jsFormData.append("consultation_id", generalConsultationInfo?.id ?? "");
      jsFormData.append("prescribed_by", generalConsultationInfo?.userId ?? "");
      jsFormData.append("patient_id", generalConsultationInfo?.patientId ?? "");
      jsFormData.append("prescription_date", new Date().toISOString());

      createPrescription(jsFormData, {
        onSuccess: res => {
          // import.meta.env.DEV && console.log(res.data);
          createMedication(
            {
              medication: { ...formData, prescriptionId: res.data._id },
              patientId: patientId as string,
            },
            {
              onSuccess: () => {
                setFormData({} as Medication);
                close();
              },
            }
          );
        },
      });
    } else {
      createMedication(
        {
          medication: { ...formData, prescriptionId },
          patientId: patientId as string,
        },
        {
          onSuccess: () => {
            setFormData({} as Medication);
            close();
          },
        }
      );
    }
  };

  useEffect(() => {
    if (formData) console.log("[Create Medication formData]", formData);
  }, [formData]);

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      title={
        <Group gap={7}>
          <Text fw={500} c="black">
            {t("add-medication-to")}
          </Text>
          <Badge bg="accent.0" c="accent.6" size="lg">
            {cId}
          </Badge>
        </Group>
      }>
      <StyledEditModalContent>
        <form className="form" style={{ marginTop: 0 }}>
          <div className="form-field">
            <label htmlFor="medication">
              {t("medication")}
              <sup>*</sup>
            </label>
            <input
              id="medication"
              type="text"
              placeholder={t("eg", { example: "Paracetamol" })}
              name="name"
              value={formData.name ?? ""}
              onChange={e => handleOnChange(e, setFormData, formData)}
            />
          </div>

          <div className="form-field">
            <label htmlFor="frequency">
              {t("frequency")}
              <sup>*</sup>
            </label>
            <input
              id="frequency"
              type="text"
              name="frequency"
              placeholder={t("eg", { example: t("3x-daily") })}
              value={formData.frequency ?? ""}
              onChange={e => handleOnChange(e, setFormData, formData)}
              required
            />
          </div>

          <div className="form-group">
            <div className="form-field">
              <label htmlFor="dosage">
                {t("dosage")}
                <sup>*</sup>
              </label>
              <input
                type="text"
                placeholder={t("eg", { example: "50 ml" })}
                id="dosage"
                name="dosage"
                value={formData.dosage ?? ""}
                onChange={e => handleOnChange(e, setFormData, formData)}
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="duration">
                {t("duration")}
                <sup>*</sup>
              </label>
              <input
                type="text"
                placeholder={t("eg", { example: t("2-weeks") })}
                id="duration"
                name="duration"
                value={formData.duration ?? ""}
                onChange={e => handleOnChange(e, setFormData, formData)}
                required
              />
            </div>
          </div>

          <div className="form-field">
            <label htmlFor="reason">{t("reason")}</label>
            <textarea
              rows={5}
              placeholder={t("type-reason")}
              id="reason"
              name="reason"
              value={formData.reason ?? ""}
              onChange={e =>
                handleOnChange(e, setFormData, formData)
              }></textarea>
          </div>
        </form>

        <Group justify="end" mt="lg">
          <PermissionGate permission="medications.create"><Button
            onClick={handleCreateMedication}
            disabled={
              !formData?.name ||
              !formData?.frequency ||
              !formData.dosage ||
              !formData.duration
            }
            loading={isCreateMedicationLoading || isCreatePrescriptionLoading}>
            {t("add-medication")}
          </Button></PermissionGate>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
